import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBooking } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Phone, Mail, Globe, MapPin, Hotel, FileText, Package, Hash, Clock, Calendar, Anchor } from "lucide-react";

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  let cls = "status-badge status-default";
  if (s === "CONFIRMED" || s === "ARRIVED") cls = "status-badge status-confirmed";
  else if (s === "CANCELLED") cls = "status-badge status-cancelled";
  else if (s === "RESERVED") cls = "status-badge status-reserved";
  return <span className={cls}>{status || "N/A"}</span>;
}

function InfoRow({ icon: Icon, label, value, color = "text-blue-400" }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/[0.04] last:border-0">
      <div className={`w-7 h-7 rounded-lg bg-white/[0.03] flex items-center justify-center shrink-0`}>
        <Icon className={`w-3.5 h-3.5 ${color}`} />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600">{label}</div>
        <div className="text-sm text-white mt-0.5">{value}</div>
      </div>
    </div>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getBooking(id);
        setBooking(data.booking);
        setExtras(data.extras || []);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div>
        <div className="page-header"><div className="h-6 bg-white/5 animate-pulse w-48 rounded" /></div>
        <div className="page-body">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card p-5 h-48">
                <div className="h-4 bg-white/5 animate-pulse w-32 rounded mb-4" />
                <div className="space-y-3">
                  <div className="h-3 bg-white/5 animate-pulse rounded" />
                  <div className="h-3 bg-white/5 animate-pulse rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div>
        <div className="page-header">
          <h1 className="text-2xl font-bold tracking-tight text-white">Bulunamadi</h1>
        </div>
        <div className="page-body">
          <Button variant="outline" onClick={() => navigate("/bookings")}
            className="rounded-lg gap-2 border-white/10 text-slate-300 hover:bg-white/[0.06]">
            <ArrowLeft className="w-4 h-4" /> Geri Don
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-detail-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" data-testid="back-to-bookings-button"
            onClick={() => navigate("/bookings")}
            className="rounded-lg h-9 w-9 p-0 border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-white">
                {booking.confirmation_code || `#${booking.bokun_booking_id}`}
              </h1>
              <StatusBadge status={booking.booking_status} />
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {booking.product_name}
              {booking.option_name && booking.option_name !== booking.product_name ? ` - ${booking.option_name}` : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Customer */}
          <div className="glass-card p-5" data-testid="customer-info-card">
            <div className="stat-label mb-3">Musteri Bilgileri</div>
            <InfoRow icon={User} label="Isim" value={booking.customer_name} />
            <InfoRow icon={Mail} label="Email" value={booking.customer_email} color="text-emerald-400" />
            <InfoRow icon={Phone} label="Telefon" value={booking.customer_phone} color="text-amber-400" />
            <InfoRow icon={Globe} label="Ulke" value={booking.customer_country} color="text-purple-400" />
          </div>

          {/* Pax */}
          <div className="glass-card p-5" data-testid="pax-breakdown-card">
            <div className="stat-label mb-3">Yolcu Dagilimi</div>
            <div className="pax-grid">
              <div className="pax-item" data-testid="pax-total">
                <div className="pax-count text-blue-400">{booking.total_pax || 0}</div>
                <div className="pax-label">Toplam</div>
              </div>
              <div className="pax-item" data-testid="pax-adult">
                <div className="pax-count">{booking.adult_count || 0}</div>
                <div className="pax-label">Yetiskin</div>
              </div>
              <div className="pax-item" data-testid="pax-child">
                <div className="pax-count">{booking.child_count || 0}</div>
                <div className="pax-label">Cocuk</div>
              </div>
              <div className="pax-item" data-testid="pax-infant">
                <div className="pax-count">{booking.infant_count || 0}</div>
                <div className="pax-label">Bebek</div>
              </div>
            </div>
          </div>

          {/* Booking Info */}
          <div className="glass-card p-5" data-testid="booking-info-card">
            <div className="stat-label mb-3">Rezervasyon Detaylari</div>
            <InfoRow icon={Hash} label="Rez. No" value={booking.confirmation_code || `#${booking.bokun_booking_id}`} />
            <InfoRow icon={Anchor} label="Bokun ID" value={booking.bokun_booking_id} color="text-cyan-400" />
            <InfoRow icon={Package} label="Urun" value={booking.product_name} />
            <InfoRow icon={FileText} label="Opsiyon" value={booking.option_name} color="text-orange-400" />
            <InfoRow icon={Calendar} label="Tarih" value={booking.booking_date || "N/A"} color="text-purple-400" />
            <InfoRow icon={Clock} label="Saat" value={booking.booking_time} color="text-sky-400" />
            <InfoRow icon={Hotel} label="Otel" value={booking.hotel_name} color="text-amber-400" />
            <InfoRow icon={MapPin} label="Alinma Noktasi" value={booking.pickup_location} color="text-emerald-400" />
          </div>
        </div>

        {/* Extras */}
        {extras.length > 0 && (
          <div className="glass-card p-5 mt-4" data-testid="extras-card">
            <div className="stat-label mb-3">Ekstralar</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {extras.map((e, i) => (
                <div key={i} data-testid={`extra-row-${i}`} className="bg-black/20 border border-white/[0.04] rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-300">{e.extra_name || "Extra #" + (e.extra_id || i+1)}</span>
                  <span className="text-sm font-bold text-blue-400 font-mono">x{e.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {booking.notes && (
          <div className="glass-card p-5 mt-4" data-testid="notes-card">
            <div className="stat-label mb-3">Notlar</div>
            <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed bg-black/20 border border-white/[0.04] rounded-lg p-4">
              {booking.notes}
            </div>
          </div>
        )}

        {/* Raw Data */}
        {booking.raw_data && (
          <details className="glass-card mt-4" data-testid="raw-data-section">
            <summary className="p-4 cursor-pointer text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 hover:bg-white/[0.02]">
              Ham API Verisi
            </summary>
            <div className="p-4 border-t border-white/[0.04]">
              <pre className="text-xs font-mono text-slate-500 overflow-auto max-h-96 bg-black/30 p-4 rounded-lg">
                {JSON.stringify(booking.raw_data, null, 2)}
              </pre>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
